import loadable from "@loadable/component";
import classnames from "classnames";
import { isLeft } from "fp-ts/lib/Either";
import i18n from "i18next";
import { inject } from "mobx-react";
import React from "react";
import type { Store } from "../../../../client/stores";
import { Byline } from "../../components/Byline.component";
import { PayloadResolverLegacy } from "../../decoders/Content.types";
import * as Webapi from "../../generated/webapi.g";
import { getPaths } from "../../lib/io-ts-validator";
import { logger } from "../../lib/logging";
import { Heading } from "../../ui";
import "./FrontMatter.style.scss";

const EyebrowLoader = loadable(() => import("./EyebrowLoader.component"));
const Eyebrow = loadable(() => import("../../components/Eyebrow.component"));

const log = logger.getChild("FrontMatter");

interface FrontMatterProps {
    content: Webapi.PublishRecordFragment;
    block: Webapi.Block_SitesItemBlock_Fragment;
    eyebrow?: Webapi.Eyebrow;
    store?: Store;
}

export const FrontMatter = inject("store")(({ content, block, store }: FrontMatterProps) => {
    if (!store || !content || !block) {
        return null;
    }
    const resolvedPayload = PayloadResolverLegacy.decode(content.payload);
    if (isLeft(resolvedPayload)) {
        const errorMessage = `Payload did not validate! ${getPaths(resolvedPayload)}`;
        log.error(errorMessage);
        return null;
    }
    const payload = resolvedPayload.right;
    let eyebrow;
    if (payload.is_sponsored) {
        eyebrow = {
            topic_name: i18n.t("sponsored_content"),
        };
    } else if (payload.feed_vendor) {
        eyebrow = {
            topic_name: i18n.t("partner_content"),
        };
    }

    if (eyebrow) {
        store.storeBlock({
            ...block,
            type: "item",
            eyebrow,
        });
    }

    const headerCss = classnames("content-header", {
        "front-matter": block.tags?.includes("display-hints/front-matter"),
    });

    return (
        <header className={headerCss}>
            {eyebrow ? <Eyebrow {...eyebrow} /> : <EyebrowLoader content={content} block={block} />}
            <Heading level={1}>{content.summary.title}</Heading>
            <hr />
            <Byline
                publish_date={content.pub_start}
                user={content.summary.author}
                image={content.summary.image}
                feed_vendor={payload.feed_vendor || ""}
            />
        </header>
    );
});

export default FrontMatter;
